import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, List, ListItem,
  ListItemText, ListItemIcon, Chip, Divider, Alert, CircularProgress,
  IconButton
} from '@mui/material';
import {
  Sync, CloudUpload, CloudDone, CloudOff, Schedule,
  Delete, LocalGasStation, Refresh
} from '@mui/icons-material';
import useOfflineSync from '../hooks/useOfflineSync';
import * as offlineDb from '../services/offlineDb';

export default function SyncStatusPage() {
  const { pendingCount, isOnline, isSyncing, lastSync, syncError, syncNow, downloadData } = useOfflineSync();
  const [pendingReadings, setPendingReadings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const pending = await offlineDb.getPendingReadings();
      setPendingReadings(pending || []);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { loadPending(); }, [loadPending, pendingCount]);

  const handleSync = async () => {
    await syncNow();
    await loadPending();
  };

  const handleDownload = async () => {
    await downloadData();
  };

  const handleDeletePending = async (localId) => {
    try {
      await offlineDb.deletePendingReading(localId);
      await loadPending();
    } catch (_) {}
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Connection Status */}
      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {isOnline ? <CloudDone color="success" /> : <CloudOff color="error" />}
            <Typography variant="h6" fontWeight={600}>
              {isOnline ? 'Online' : 'Offline'}
            </Typography>
          </Box>
          {lastSync && (
            <Typography variant="body2" color="text.secondary">
              <Schedule sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Ultima sincronizzazione: {formatDate(lastSync)}
            </Typography>
          )}
        </CardContent>
      </Card>

      {syncError && <Alert severity="error" sx={{ mb: 2 }}>{syncError}</Alert>}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSync}
          disabled={isSyncing || !isOnline}
          startIcon={isSyncing ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
          sx={{ borderRadius: 2 }}
        >
          {isSyncing ? 'Sincronizzazione...' : `Sincronizza${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownload}
          disabled={isSyncing || !isOnline}
          sx={{ borderRadius: 2, minWidth: 'auto', px: 2 }}
        >
          <Refresh />
        </Button>
      </Box>

      {/* Pending Readings */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Letture in attesa ({pendingReadings.filter(r => !r.synced).length})
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : pendingReadings.filter(r => !r.synced).length === 0 ? (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <CloudDone sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography color="text.secondary">Tutto sincronizzato</Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ borderRadius: 2 }}>
          <List disablePadding>
            {pendingReadings.filter(r => !r.synced).map((reading, idx) => (
              <React.Fragment key={reading.localId || idx}>
                {idx > 0 && <Divider />}
                <ListItem
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => handleDeletePending(reading.localId)}>
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LocalGasStation color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {reading.plantName || `Impianto #${reading.plantId}`}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${reading.readings?.length || 0} letture`}
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </Box>
                    }
                    secondary={formatDate(reading.timestamp)}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}

      {/* Synced Readings */}
      {pendingReadings.filter(r => r.synced).length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            Sincronizzate di recente
          </Typography>
          <Card sx={{ borderRadius: 2 }}>
            <List disablePadding>
              {pendingReadings.filter(r => r.synced).slice(0, 10).map((reading, idx) => (
                <React.Fragment key={reading.localId || idx}>
                  {idx > 0 && <Divider />}
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Sync color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={reading.plantName || `Impianto #${reading.plantId}`}
                      secondary={formatDate(reading.timestamp)}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Card>
        </>
      )}
    </Box>
  );
}
